import { GridColDef } from "@mui/x-data-grid-premium";
import {
  Groups,
  Person,
  Business,
  CheckCircle,
  SupportAgent
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { useMemo } from "react";
import EventNote from "@mui/icons-material/EventNote";
import { TradingCounterPartySummary } from "../../../../../api/Api";
import {
  dateColumn,
  operationalReadinessColumn
} from "../../../../../components/datagrid/overviewCells";

const UNASSIGNED = "Unassigned";

const useHandlerOptions = (rows: TradingCounterPartySummary[]) =>
  useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      set.add(row.marketAccessHandler || UNASSIGNED);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

const formatSignedOffAt = (value: string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit"
  });
};

export const useCompletedCounterpartyColumns = (
  rows: TradingCounterPartySummary[] = [],
  showMarketAccessHandler = false
): GridColDef<TradingCounterPartySummary>[] => {
  const handlerOptions = useHandlerOptions(rows);
  return useMemo(() => {
    const columns: GridColDef<TradingCounterPartySummary>[] = [
      {
        field: "counterpartyName",
        headerName: "Counterparty",
        width: 300,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Business fontSize="small" />
            Counterparty
          </Box>
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Groups fontSize="small" />
            Trading Team
          </Box>
        )
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "Requester",
        width: 250,
        sortable: true,
        filterable: true,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Person fontSize="small" />
            MFT Contact Person
          </Box>
        )
      },
      dateColumn<TradingCounterPartySummary>(
        "requestedAt",
        "Requested",
        (row) => row.requestedAt,
        <EventNote fontSize="small" />
      ),
      ...(showMarketAccessHandler
        ? [
            {
              field: "marketAccessHandler",
              headerName: "MA Handler",
              width: 220,
              sortable: true,
              filterable: true,
              type: "singleSelect",
              valueOptions: handlerOptions,
              valueGetter: (_value, row) =>
                row.marketAccessHandler || UNASSIGNED,
              renderHeader: () => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SupportAgent fontSize="small" />
                  MA Handler
                </Box>
              )
            } as GridColDef<TradingCounterPartySummary>
          ]
        : []),
      operationalReadinessColumn<TradingCounterPartySummary>(
        (row) => row.operationalReadinessStatus
      ),
      {
        field: "signOffCompletedAt",
        headerName: "Signed off at",
        width: 180,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) => row.signOffCompletedAt || null,
        renderCell: (params) => formatSignedOffAt(params.value),
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle fontSize="small" color="success" />
            Signed off at
          </Box>
        )
      }
    ];
    return columns;
  }, [handlerOptions, showMarketAccessHandler]);
};
